import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FileSystemItem } from "types";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import Card from "components/card";
import Loader from "components/Loader";
import toast from "react-hot-toast";
import api, {
  getDocumentContent,
  regenerateDocumentContent,
  saveDocumentContent,
} from "api/axios";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import { useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";

GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const DeepExtractFileView = ({
  file,
  page = 1,
}: {
  file: FileSystemItem;
  page?: number;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const deepExtractId = searchParams.get("deepExtract");
  const [pdfDocument, setPdfDocument] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(page);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canvasRef, setCanvasRef] = useState<HTMLCanvasElement | null>(null);
  const [instruction, setInstruction] = useState<string>("");

  const parsedMetadata = useMemo(() => {
    if (!file?.document_metadata) return null;
    const metadata = file.document_metadata;
    if (typeof metadata === "string") {
      try {
        return JSON.parse((metadata as string)?.replace(/'/g, '"'));
      } catch (e) {
        console.error("Failed to parse document_metadata:", e);
        return null;
      }
    }
    return metadata;
  }, [file?.document_metadata]);

  const isPdf = parsedMetadata?.file_type === "pdf";
  const [documentContent, setDocumentContent] = useState<
    | {
        page_number: number;
        extraction_content: string;
        isChanged?: boolean;
      }[]
    | null
  >(null);
  const [extractPageLoading, setExtractPageLoading] = useState(false);

  const fetchPdf = useCallback(
    async (abortController?: AbortController) => {
      if (!file || !isPdf) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await api.get(`/documents/${file.id}/download`, {
          responseType: "arraybuffer",
          signal: abortController?.signal,
        });

        const pdf = await getDocument({
          data: response.data,
          cMapUrl: "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/",
          cMapPacked: true,
        }).promise;

        setPdfDocument(pdf);
        setTotalPages(pdf.numPages);
        setCurrentPage(1);
      } catch (err: any) {
        if (err.name === "CanceledError") {
          return;
        }
        setError("Failed to load PDF preview.");
        console.error("PDF fetch error:", err);
      } finally {
        setLoading(false);
      }
    },
    [file, isPdf]
  );

  const renderPage = useCallback(
    async (pageNum: number) => {
      if (!pdfDocument || !canvasRef) return;

      try {
        const page = await pdfDocument.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1.5 });

        const canvas = canvasRef;
        const context = canvas.getContext("2d");

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        await page.render(renderContext).promise;
      } catch (err) {
        console.error("Error rendering page:", err);
      }
    },
    [pdfDocument, canvasRef]
  );

  useEffect(() => {
    if (pdfDocument && canvasRef) {
      renderPage(currentPage);
    }
  }, [pdfDocument, canvasRef, currentPage, renderPage]);

  useEffect(() => {
    return () => {
      try {
        pdfDocument?.destroy();
      } catch (e) {}
    };
  }, [pdfDocument]);

  const handlePreviousPage = () => {
    if (extractPageLoading) return;
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (extractPageLoading) return;
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleRetry = () => {
    fetchPdf();
  };

  useEffect(() => {
    const abortController = new AbortController();
    fetchPdf(abortController);

    return () => {
      abortController.abort();
    };
  }, [file, fetchPdf]);

  useEffect(() => {
    if (!deepExtractId) return;
    getDocumentContent(deepExtractId).then((response) => {
      if (!response?.success) return setDocumentContent(null);
      setDocumentContent(response?.pages);
    });
  }, [deepExtractId]);

  const handleExtractPage = async () => {
    if (!instruction.trim()) {
      toast.error("Please enter an instruction before extracting.");
      return;
    }
    setExtractPageLoading(true);

    try {
      const response = await regenerateDocumentContent(
        deepExtractId,
        instruction,
        currentPage
      );
      setDocumentContent((prevContent) =>
        prevContent?.map((item) =>
          item.page_number === currentPage
            ? {
                ...item,
                extraction_content: response.content,
                isChanged: true,
              }
            : item
        )
      );

      toast.success("Page extracted successfully!");
    } catch (error) {
      console.error("extract failed", error);
      toast.error("Failed to extract page.");
    } finally {
      setExtractPageLoading(false);
      setInstruction("");
    }
  };

  const handleSave = async () => {
    if (!documentContent?.some((item) => item.isChanged) || extractPageLoading)
      return;
    try {
      const saveDeepExtract = await saveDocumentContent(
        deepExtractId,
        documentContent
      );
      if (saveDeepExtract?.success) {
        toast.success("Saved successfully!");
      }
      setSearchParams((params) => {
        params.delete("deepExtract");
        return params;
      });
    } catch (error) {
      toast.error("Failed to save.");
    }
  };

  if (!file) {
    return null;
  }

  const renderFilePreview = () => {
    if (!isPdf)
      return (
        <span className="text-gray-500 dark:text-gray-400">
          Preview not available for this file type.
        </span>
      );

    if (loading) return <Loader txt="Loading PDF preview..." />;
    if (error)
      return (
        <div className="flex flex-col items-center justify-center space-y-4">
          <span className="text-center text-red-500 dark:text-red-400">
            {error}
          </span>
          <button
            onClick={handleRetry}
            className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-100 hover:text-blue-700 dark:border-gray-700 dark:bg-navy-800 dark:text-gray-200 dark:hover:bg-navy-700 dark:hover:text-blue-400"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Retry</span>
          </button>
        </div>
      );
    if (!pdfDocument)
      return (
        <span className="text-gray-500 dark:text-gray-400">
          No preview available.
        </span>
      );

    return (
      <div className="flex h-full w-full flex-col items-center gap-2">
        {/* PDF Canvas */}
        <div className="flex h-full w-full items-center justify-center">
          <canvas
            ref={setCanvasRef}
            className="h-[calc(93vh-42px)] max-w-full rounded-lg border shadow-lg"
          />
        </div>
        {/* Page Navigation */}
        <div className="mb-2 flex items-center space-x-4">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage <= 1 || extractPageLoading}
            className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-100 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-navy-800 dark:text-gray-200 dark:hover:bg-navy-700 dark:hover:text-blue-400"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>Previous</span>
          </button>

          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={handleNextPage}
            disabled={currentPage >= totalPages || extractPageLoading}
            className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-100 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-navy-800 dark:text-gray-200 dark:hover:bg-navy-700 dark:hover:text-blue-400"
          >
            <span>Next</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  const existDocumentChange = documentContent?.some((item) => item.isChanged);

  return (
    <div className="flex h-screen max-h-[93vh] min-w-[79vw] gap-6 px-4">
      <div className="flex h-full min-h-[400px] w-1/2 flex-col items-center justify-center rounded-lg border-none">
        {renderFilePreview()}
      </div>
      <Card extra="flex w-1/2 max-h-max flex-col p-6">
        <div className="flex h-full flex-col space-y-4">
          <div className="flex flex-[2] flex-col">
            <p className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              Extracted Text:{" "}
              <span
                className={
                  "text-sm font-medium text-gray-700 dark:text-gray-300"
                }
              >
                Displays the AI-generated summary of the PDF page — you can edit
                it directly and press <strong>Save</strong> to store your
                changes.
              </span>
            </p>
            <textarea
              disabled={extractPageLoading}
              aria-busy={extractPageLoading}
              value={
                documentContent?.find(
                  (item) => item.page_number === currentPage
                )?.extraction_content ?? ""
              }
              onChange={(e) => {
                setDocumentContent((prevContent) => {
                  return prevContent?.map((item) => {
                    if (item.page_number === currentPage) {
                      return {
                        ...item,
                        extraction_content: e.target.value,
                        isChanged: true,
                      };
                    }
                    return item;
                  });
                });
              }}
              placeholder="Extracted text will appear here..."
              className={cn(
                "w-full flex-1 resize-none rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white",
                {
                  "cursor-not-allowed opacity-50": extractPageLoading,
                }
              )}
            />
          </div>

          <div className="flex flex-[1] flex-col">
            <p className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              Instruction:{" "}
              <span
                className={
                  "text-sm font-medium text-gray-700 dark:text-gray-300"
                }
              >
                Enter a prompt for the AI to generate/update the Extracted Text,
                then press <strong>Extract Page</strong>.
              </span>
            </p>
            <textarea
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder="Enter your instruction here..."
              className="w-full flex-1 resize-none rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleExtractPage}
              disabled={!instruction.trim() || extractPageLoading}
              aria-busy={extractPageLoading}
              className={cn(
                "linear flex-1 cursor-pointer rounded-xl bg-brand-900 px-6 py-2 font-medium text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90",
                {
                  "cursor-not-allowed opacity-50":
                    !instruction?.trim() || extractPageLoading,
                }
              )}
            >
              {extractPageLoading ? (
                <span className="inline-flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Extracting...
                </span>
              ) : (
                <span>Extract Page</span>
              )}
            </button>
            <button
              disabled={!existDocumentChange || extractPageLoading}
              onClick={handleSave}
              className={cn(
                "linear flex-1 cursor-pointer rounded-xl bg-brand-900 px-6 py-2 font-medium text-white transition duration-200 hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90",
                {
                  "cursor-not-allowed opacity-50":
                    !existDocumentChange || extractPageLoading,
                }
              )}
            >
              <span>Save</span>
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default DeepExtractFileView;
